/**
 * useSounds — tiny synth library that plays Robinhood-style emotional
 * micro-cues without shipping any MP3s (Web Audio API + oscillators).
 *
 * Why no MP3s?
 *   1) Zero asset weight (saves ~80KB across 4 cues).
 *   2) Consistent cross-platform timbre (no codec quirks).
 *   3) Volume / pitch / sustain are pure JS — easy to A/B test later.
 *
 * Volume is muted by default to respect user agency on first session.
 * The first call to `prime()` (gated by a user gesture) unlocks audio
 * on iOS Safari per the autoplay policy. A user-facing toggle lives in
 * Settings → Notifications → "Subtle sound cues" (default OFF in this
 * iteration; we'll flip to opt-out once we've measured retention lift).
 *
 * Public API:
 *   const { play, enabled, setEnabled, prime } = useSounds();
 *   play('lecture-saved');
 *
 * Cues:
 *   • 'lecture-saved'    — major-third bell (C5 → E5), 240ms
 *   • 'card-correct'     — short ping (E5), 120ms
 *   • 'streak-saved'     — rising arpeggio (C5 E5 G5), 280ms
 *   • 'achievement'      — celebratory triad (G5 B5 D6), 360ms
 *   • 'recording-start'  — soft down-chirp (E5 → C5), 180ms
 *   • 'recording-stop'   — soft up-chirp (C5 → E5), 180ms
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const STORAGE_KEY = 'isp_sound_cues_enabled';

// Frequency table — only the notes we use.
const F = {
  C5: 523.25, E5: 659.25, G5: 783.99,
  B5: 987.77, D6: 1174.66,
};

/**
 * A single note plays as a sine wave with a short attack/decay envelope
 * so it lands as a soft "ding" instead of a click.
 */
const playNote = (ctx, freq, when, duration = 0.18, volume = 0.12) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  // ASR envelope: 20ms attack, exp decay over `duration`.
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(volume, when + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(when);
  osc.stop(when + duration + 0.02);
};

const playGlide = (ctx, fromF, toF, when, duration = 0.16, volume = 0.1) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(fromF, when);
  osc.frequency.linearRampToValueAtTime(toF, when + duration);
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(volume, when + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(when);
  osc.stop(when + duration + 0.02);
};

const CUES = {
  'lecture-saved': (ctx, t) => {
    playNote(ctx, F.C5, t, 0.20, 0.10);
    playNote(ctx, F.E5, t + 0.07, 0.24, 0.10);
  },
  'card-correct': (ctx, t) => {
    playNote(ctx, F.E5, t, 0.12, 0.10);
  },
  'streak-saved': (ctx, t) => {
    playNote(ctx, F.C5, t, 0.14, 0.10);
    playNote(ctx, F.E5, t + 0.08, 0.14, 0.10);
    playNote(ctx, F.G5, t + 0.18, 0.20, 0.11);
  },
  'achievement': (ctx, t) => {
    playNote(ctx, F.G5, t, 0.16, 0.12);
    playNote(ctx, F.B5, t + 0.10, 0.16, 0.12);
    playNote(ctx, F.D6, t + 0.22, 0.24, 0.12);
  },
  'recording-start': (ctx, t) => {
    playGlide(ctx, F.E5, F.C5, t, 0.18, 0.08);
  },
  'recording-stop': (ctx, t) => {
    playGlide(ctx, F.C5, F.E5, t, 0.18, 0.08);
  },
};

let _sharedCtx = null;
const getCtx = () => {
  if (typeof window === 'undefined') return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!_sharedCtx) _sharedCtx = new AC();
  return _sharedCtx;
};

export const useSounds = () => {
  const [enabled, setEnabledState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // OPT-IN for now — we'll move to opt-out after we measure lift.
      return stored === 'true';
    } catch { return false; }
  });
  const primedRef = useRef(false);

  const setEnabled = useCallback((v) => {
    setEnabledState(v);
    try { localStorage.setItem(STORAGE_KEY, String(v)); } catch { /* ignore */ }
  }, []);

  const prime = useCallback(async () => {
    if (primedRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      try { await ctx.resume(); } catch { /* ignore */ }
    }
    primedRef.current = true;
  }, []);

  // Auto-prime on first user gesture so we never miss the first cue
  // after enabling sounds (iOS Safari autoplay policy).
  useEffect(() => {
    if (!enabled) return undefined;
    const onGesture = () => { prime(); };
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, [enabled, prime]);

  const play = useCallback((cueName) => {
    if (!enabled) return;
    const cue = CUES[cueName];
    if (!cue) return;
    const ctx = getCtx();
    if (!ctx) return;
    // Resume if suspended (rare on subsequent calls but cheap).
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    try {
      cue(ctx, ctx.currentTime);
    } catch (e) {
      // Audio failures should never escalate — just swallow silently.
      console.warn('sound cue failed:', e?.message);
    }
  }, [enabled]);

  return useMemo(() => ({ play, enabled, setEnabled, prime }), [play, enabled, setEnabled, prime]);
};
